import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { 
  Users, 
  UserPlus,
  Clock,
  ShieldCheck,
  Plus,
  Copy,
  Eye,
  EyeOff,
  Info,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [recentSignups, setRecentSignups] = useState([]);
  const [signupsPage, setSignupsPage] = useState(0);
  const [signupsPagination, setSignupsPagination] = useState({ total: 0, total_pages: 0, has_next: false, has_prev: false });
  const [loadingSignups, setLoadingSignups] = useState(false);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminName, setNewAdminName] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [createdAdminInfo, setCreatedAdminInfo] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showCapabilitiesModal, setShowCapabilitiesModal] = useState(false);
  
  const SIGNUPS_PAGE_SIZE = 5;

  useEffect(() => {
    fetchMetrics();
    fetchAdmins();
  }, []);

  useEffect(() => {
    fetchRecentSignups();
  }, [signupsPage]);

  const fetchMetrics = async () => {
    try {
      const response = await api.get('/admin/dashboard');
      setMetrics(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await api.get('/admin/users?role=admin');
      const users = response.data.users || response.data;
      setAdmins(users);
    } catch (error) {
      console.error('Failed to fetch admins');
    }
  };

  const fetchRecentSignups = async () => {
    setLoadingSignups(true);
    try {
      const response = await api.get(`/admin/users/recent?page=${signupsPage}&limit=${SIGNUPS_PAGE_SIZE}`);
      setRecentSignups(response.data.users || []);
      setSignupsPagination({
        total: response.data.total,
        total_pages: response.data.total_pages,
        has_next: response.data.has_next,
        has_prev: response.data.has_prev
      });
    } catch (error) {
      console.error('Failed to fetch recent signups');
    } finally {
      setLoadingSignups(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/admin/search?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    
    if (!newAdminEmail || !newAdminName) {
      toast.error('Email and name are required');
      return;
    }

    setCreatingAdmin(true);
    try {
      const response = await api.post('/admin/admins', {
        email: newAdminEmail,
        name: newAdminName,
        password: newAdminPassword || undefined
      });
      
      setCreatedAdminInfo({
        email: response.data.email,
        password: response.data.password || newAdminPassword,
        name: response.data.name
      });
      
      toast.success('Admin account created!');
      fetchAdmins();
      setNewAdminEmail('');
      setNewAdminName('');
      setNewAdminPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-slate-600">Loading metrics...</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">
            Admin Dashboard
          </h1>
          <p className="text-slate-600 mt-1">Overview of Smarter Hoops platform</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowCapabilitiesModal(true)}
          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
          data-testid="admin-capabilities-info-btn"
        >
          <Info className="w-5 h-5 mr-1" />
          Admin Info
        </Button>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <Link
          to="/admin/users"
          className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all"
          data-testid="quick-action-users"
        >
          <Users className="w-8 h-8 text-blue-600 mb-2" />
          <span className="font-bold text-slate-900">View All Users</span>
        </Link>
        <Link
          to="/admin/audit-logs"
          className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-purple-300 hover:shadow-md transition-all"
          data-testid="quick-action-logs"
        >
          <Clock className="w-8 h-8 text-purple-600 mb-2" />
          <span className="font-bold text-slate-900">Audit Logs</span>
        </Link>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl border-2 border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <Users className="w-10 h-10 text-blue-600" />
            <span className="text-3xl font-bold text-slate-900">{metrics?.total_users || 0}</span>
          </div>
          <h3 className="font-bold text-slate-700 uppercase text-sm">Total Users</h3>
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            <div className="bg-slate-100 rounded px-2 py-1">
              <span className="text-slate-600">Team Mgrs:</span>{' '}
              <span className="font-bold">{(metrics?.users_by_role?.team_manager || 0) + (metrics?.users_by_role?.coach || 0)}</span>
            </div>
            <div className="bg-slate-100 rounded px-2 py-1">
              <span className="text-slate-600">Admins:</span>{' '}
              <span className="font-bold">{metrics?.users_by_role?.admin || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border-2 border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <UserPlus className="w-10 h-10 text-purple-600" />
            <span className="text-3xl font-bold text-slate-900">{metrics?.new_signups_this_week || 0}</span>
          </div>
          <h3 className="font-bold text-slate-700 uppercase text-sm">New Signups</h3>
          <p className="text-xs text-slate-500 mt-2">Last 7 days</p>
        </div>
      </div>

      {/* New Signups Section */}
      <div className="bg-white rounded-xl border-2 border-slate-200 p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 uppercase text-sm">New Signups</h3>
              <p className="text-xs text-slate-500">{signupsPagination.total} total users</p>
            </div>
          </div>
        </div>
        
        {loadingSignups ? (
          <div className="text-sm text-slate-500 py-4 text-center">Loading...</div>
        ) : recentSignups.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">No signups yet</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="new-signups-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 font-bold text-slate-700 uppercase text-xs">Name</th>
                    <th className="text-left py-3 px-2 font-bold text-slate-700 uppercase text-xs">Role</th>
                    <th className="text-left py-3 px-2 font-bold text-slate-700 uppercase text-xs">Email</th>
                    <th className="text-left py-3 px-2 font-bold text-slate-700 uppercase text-xs">Signup Date</th>
                    <th className="text-right py-3 px-2 font-bold text-slate-700 uppercase text-xs">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSignups.map(user => (
                    <tr key={user.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-2 font-medium text-slate-900">{user.name || 'No name'}</td>
                      <td className="py-3 px-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                          (user.role === 'coach' || user.role === 'team_manager') ? 'bg-blue-100 text-blue-700' :
                          user.role === 'admin' ? 'bg-amber-100 text-amber-800' :
                          user.role === 'player' ? 'bg-green-100 text-green-700' :
                          user.role === 'staff' ? 'bg-orange-100 text-orange-700' :
                          user.role === 'fan' ? 'bg-purple-100 text-purple-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {user.role === 'team_manager' ? 'Team Mgr' : user.role === 'coach' ? 'Coach (legacy)' : (user.role || 'Not set')}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-slate-600">{user.email}</td>
                      <td className="py-3 px-2 text-slate-600">{formatDate(user.created_at)}</td>
                      <td className="py-3 px-2 text-right">
                        <Link 
                          to={`/admin/users/${user.user_id}`}
                          className="text-blue-600 hover:text-blue-700 font-medium text-xs"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {signupsPagination.total_pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                <span className="text-sm text-slate-500">
                  Page {signupsPage + 1} of {signupsPagination.total_pages}
                </span>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    disabled={!signupsPagination.has_prev} 
                    onClick={() => setSignupsPage(p => p - 1)}
                    data-testid="signups-prev-btn"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Prev
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    disabled={!signupsPagination.has_next} 
                    onClick={() => setSignupsPage(p => p + 1)}
                    data-testid="signups-next-btn"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Administrators Section */}
      <div className="bg-white rounded-xl border-2 border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-8 h-8 text-indigo-600" />
            <div>
              <h3 className="font-bold text-slate-900 uppercase text-sm">Administrators</h3>
              <p className="text-xs text-slate-500">Manage admin accounts for customer support</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowCreateAdmin(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="create-admin-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Admin
          </Button>
        </div>
        
        <div className="space-y-2">
          {admins.length === 0 ? (
            <p className="text-slate-500 text-sm">No administrators found</p>
          ) : (
            admins.map((admin) => (
              <div 
                key={admin.user_id} 
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{admin.name}</p>
                    <p className="text-sm text-slate-500">{admin.email}</p>
                  </div>
                </div>
                <Link 
                  to={`/admin/users/${admin.user_id}`}
                  className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  View Details
                </Link>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Admin Dialog */}
      <Dialog open={showCreateAdmin} onOpenChange={(open) => {
        setShowCreateAdmin(open);
        if (!open) {
          setCreatedAdminInfo(null);
          setNewAdminEmail('');
          setNewAdminName('');
          setNewAdminPassword('');
        }
      }}>
        <DialogContent data-testid="create-admin-modal" className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-xl flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-indigo-600" />
              {createdAdminInfo ? 'Admin Created' : 'Create Admin Account'}
            </DialogTitle>
          </DialogHeader>
          
          {createdAdminInfo ? (
            <div className="space-y-4 py-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800 font-medium mb-2">Admin account created successfully!</p>
                <p className="text-sm text-green-700">Share these credentials securely with the new admin.</p>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-500">Name</Label>
                  <p className="font-medium">{createdAdminInfo.name}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <div className="flex items-center gap-2">
                    <p className="font-medium flex-1">{createdAdminInfo.email}</p>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => copyToClipboard(createdAdminInfo.email)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Password</Label>
                  <div className="flex items-center gap-2">
                    <p className="font-mono bg-slate-100 px-3 py-2 rounded flex-1">
                      {showPassword ? createdAdminInfo.password : '••••••••••••'}
                    </p>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => copyToClipboard(createdAdminInfo.password)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <strong>Important:</strong> This password will not be shown again. Make sure to save it securely.
              </div>
              
              <DialogFooter>
                <Button onClick={() => {
                  setShowCreateAdmin(false);
                  setCreatedAdminInfo(null);
                }}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleCreateAdmin}>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="adminName" className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                    Name *
                  </Label>
                  <Input
                    id="adminName"
                    value={newAdminName}
                    onChange={(e) => setNewAdminName(e.target.value)}
                    placeholder="John Smith"
                    required
                    className="h-12"
                    data-testid="admin-name-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="adminEmail" className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                    Email *
                  </Label>
                  <Input
                    id="adminEmail"
                    type="email"
                    value={newAdminEmail}
                    onChange={(e) => setNewAdminEmail(e.target.value)}
                    placeholder="admin@company.com"
                    required
                    className="h-12"
                    data-testid="admin-email-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="adminPassword" className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                    Password (optional)
                  </Label>
                  <Input
                    id="adminPassword"
                    type="password"
                    value={newAdminPassword}
                    onChange={(e) => setNewAdminPassword(e.target.value)}
                    placeholder="Leave blank to auto-generate"
                    className="h-12"
                    data-testid="admin-password-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">If left blank, a secure password will be generated</p>
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateAdmin(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={creatingAdmin}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="submit-create-admin"
                >
                  {creatingAdmin ? 'Creating...' : 'Create Admin'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin Capabilities Info Modal */}
      <Dialog open={showCapabilitiesModal} onOpenChange={setShowCapabilitiesModal}>
        <DialogContent data-testid="admin-capabilities-modal" className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-xl flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-600" />
              Admin Capabilities
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <ul className="text-sm text-slate-700 space-y-3">
              <li className="flex items-start gap-2">
                <Users className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>View and manage all user accounts (edit, suspend, delete)</span>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                <span>Review audit logs of admin actions and system events</span>
              </li>
            </ul>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowCapabilitiesModal(false)}>
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
